import React, { useCallback, useEffect, useState } from 'react';
import { Button, Collapse, Input, Segmented, Space, Typography, message, theme } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import api from '../api';
import { formatTS } from '../utils/time';

const TASK_OPTIONS = [
  { label: 'Open',   value: 'open' },
  { label: 'Closed', value: 'closed' },
  { label: 'N/A',    value: 'na' },
];

// The ticket's task checklist: its Request Type's tasks plus any added here.
// Queue members only -- the page renders it for them alone, and the API
// refuses anyone else. Opens by itself when there are tasks, folded when
// there are none; the header counts tasks done (Closed or N/A). Shown even
// with no tasks, so one can be added.
export default function TicketTasksCard({ ticketId, onChanged }) {
  const { token } = theme.useToken();
  const [tasks, setTasks] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [newName, setNewName]   = useState('');
  const [adding, setAdding]     = useState(false);
  const [open, setOpen]         = useState(null);    // null until the tasks are first read

  const addTask = async () => {
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await api.post(`/api/helpdesk/${ticketId}/tasks`, { name });
      setNewName('');
      load();
      onChanged?.();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to add the task.');
    } finally {
      setAdding(false);
    }
  };

  const load = useCallback(() => {
    api.get(`/api/helpdesk/${ticketId}/tasks`).then((r) => {
      setTasks(r.data || []);
      setOpen((o) => o ?? ((r.data || []).length ? ['tasks'] : []));
    }).catch(() => setTasks([]));
  }, [ticketId]);
  useEffect(() => { setOpen(null); load(); }, [load]);

  const setStatus = async (task, status) => {
    setSavingId(task.ID);
    try {
      await api.put(`/api/helpdesk/${ticketId}/tasks/${task.ID}`, { status });
      load();
      onChanged?.();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to update the task.');
    } finally {
      setSavingId(null);
    }
  };

  const done = tasks.filter((t) => t.Status !== 'open').length;

  return (
    <Collapse
      activeKey={open || []}
      onChange={setOpen}
      style={{ marginBottom: 16, background: token.colorBgContainer }}
      items={[{
        key: 'tasks',
        label: <strong>{tasks.length ? `Tasks (${done} of ${tasks.length} done)` : 'Tasks'}</strong>,
        children: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {tasks.map((t) => (
              <div key={t.ID} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <Typography.Text delete={t.Status !== 'open'} type={t.Status === 'na' ? 'secondary' : undefined}>
                    {t.Name}
                  </Typography.Text>
                  {t.UpdatedByName && (
                    <Typography.Text type="secondary" style={{ display: 'block', fontSize: 11 }}>
                      {t.UpdatedByName} &middot; {formatTS(t.UpdatedAt)}
                    </Typography.Text>
                  )}
                </div>
                <Segmented
                  size="small"
                  options={TASK_OPTIONS}
                  value={t.Status}
                  disabled={savingId === t.ID}
                  onChange={(v) => setStatus(t, v)}
                />
              </div>
            ))}
            <Space.Compact style={{ width: '100%', maxWidth: 520, marginTop: tasks.length ? 4 : 0 }}>
              <Input
                placeholder="Add a task to this ticket..."
                maxLength={200}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onPressEnter={addTask}
              />
              <Button icon={<PlusOutlined />} onClick={addTask} loading={adding} disabled={!newName.trim()}>Add</Button>
            </Space.Compact>
          </div>
        ),
      }]}
    />
  );
}
